// =============================================================================
// components/KpiRules/KpiRuleForm.jsx
//
// Rule editing panel, simple and direct:
//   - Header: "selected rule" (technology + name), always visible.
//   - "Overview" tab: read-only summary grid + Duplicate action.
//   - "Data Source & Formulas" tab: table, columns and SQL expressions.
//   - "Detection" tab: alarm criteria, filters and severity.
// =============================================================================
import { useEffect, useMemo, useState } from 'react';
import {
	Box, Grid, TextField, MenuItem, FormControlLabel, Switch,
	Button, Typography, Divider, Alert, CircularProgress, Tooltip,
	Chip, Stack, Collapse, Tabs, Tab,
} from '@mui/material';
import SaveIcon              from '@mui/icons-material/Save';
import CloseIcon             from '@mui/icons-material/Close';
import DeleteIcon            from '@mui/icons-material/Delete';
import ContentCopyIcon       from '@mui/icons-material/ContentCopy';
import CheckCircleIcon       from '@mui/icons-material/CheckCircle';
import PlayArrowIcon         from '@mui/icons-material/PlayArrow';
import InfoOutlinedIcon      from '@mui/icons-material/InfoOutlined';
import AutoAwesomeIcon       from '@mui/icons-material/AutoAwesome';
import DashboardIcon         from '@mui/icons-material/SpaceDashboard';
import FunctionsIcon         from '@mui/icons-material/Functions';
import TuneIcon              from '@mui/icons-material/Tune';

import {
	TECH_OPTIONS, DIRECTION_OPTIONS, DETECTION_METHOD_OPTIONS,
	SEVERITY_OPTIONS, KPI_PROFILES, TOOLTIPS,
} from '../../constants/kpiConstants';

// ----------------------------------------------------------------------------
// Field label with an info icon (detailed explanation shown in a tooltip)
// ----------------------------------------------------------------------------
function LabelWithTooltip({ text, tooltip }) {
	return (
		<Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
			<span>{text}</span>
			<Tooltip
				title={
					<Box sx={{ whiteSpace: 'pre-line', fontSize: '0.78rem', p: 0.5, maxWidth: 360 }}>
						{tooltip}
					</Box>
				}
				placement="top"
				arrow
			>
				<InfoOutlinedIcon sx={{ fontSize: 14, color: 'text.disabled', cursor: 'help' }} />
			</Tooltip>
		</Box>
	);
}

function SubGroupLabel({ children }) {
	return (
		<Grid item xs={12}>
			<Typography
				variant="caption"
				fontWeight={700}
				color="primary.main"
				sx={{ textTransform: 'uppercase', letterSpacing: 0.6, fontSize: '0.68rem' }}
			>
				{children}
			</Typography>
		</Grid>
	);
}

// ----------------------------------------------------------------------------
// Label/value pair for the summary grid (Overview)
// ----------------------------------------------------------------------------
function SummaryField({ label, children }) {
	return (
		<Grid item xs={6} sm={4}>
			<Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.4, fontSize: '0.68rem' }} display="block">
				{label}
			</Typography>
			<Typography variant="body2" fontWeight={600} sx={{ mt: 0.25 }}>
				{children}
			</Typography>
		</Grid>
	);
}

// ============================================================================
// Main component
// ============================================================================
export default function KpiRuleForm({
	rule, saving, saveError, validation,
	onSave, onCancel, onValidate, onDelete,
}) {
	const [form, setForm] = useState(rule);
	const [tab, setTab] = useState(0);
	const [appliedPreset, setAppliedPreset] = useState(null);

	useEffect(() => { setForm(rule); setTab(0); setAppliedPreset(null); }, [rule]);

	const isNew = form.kpiId === 0;

	const set = (field) => (e) => {
		const val = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
		setForm((prev) => ({ ...prev, [field]: val }));
	};
	const setNum = (field) => (e) => {
		setForm((prev) => ({ ...prev, [field]: Number(e.target.value) }));
	};
	const setBool = (field) => (e) => {
		setForm((prev) => ({ ...prev, [field]: e.target.value === 'true' }));
	};

	const applyPreset = (presetKey) => {
		const p = KPI_PROFILES.find((x) => x.key === presetKey);
		if (!p) return;
		setForm((prev) => ({ ...prev, ...p.preset }));
		setAppliedPreset(p.label);
	};

	const handleSave = () => onSave(form);
	const handleDuplicate = () => {
		onSave({ ...form, kpiId: 0, kpiName: `${form.kpiName} (copy)`, active: false });
	};
	const handleValidate = () => onValidate({
		sourceTable:     form.sourceTable,
		sourceVendorCol: form.sourceVendorCol,
		sourceSiteCol:   form.sourceSiteCol,
		sourceCellCol:   form.sourceCellCol,
		sourceDateCol:   form.sourceDateCol,
		kpiFormula:      form.kpiFormula,
		volFormula:      form.volFormula,
	});

	const selectedMethod = DETECTION_METHOD_OPTIONS.find((m) => m.value === form.detectionMethod);
	const selectedSeverity = useMemo(() => SEVERITY_OPTIONS.find((s) => s.value === form.severity), [form.severity]);
	const selectedDirection = DIRECTION_OPTIONS.find((o) => o.value === form.direction);

	return (
		<Box>
			{/* ============================================ */}
			{/* Header — "selected rule"                      */}
			{/* ============================================ */}
			<Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
				{isNew ? 'New rule' : 'Selected rule'}
			</Typography>

			<Stack direction="row" spacing={1.25} alignItems="center" sx={{ mt: 0.5, mb: 1 }}>
				<TextField
					select variant="outlined" size="small"
					value={form.tech} onChange={set('tech')}
					sx={{ width: 76 }}
				>
					{TECH_OPTIONS.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
				</TextField>
				<TextField
					variant="standard"
					value={form.kpiName}
					onChange={set('kpiName')}
					placeholder="KPI name"
					fullWidth
					InputProps={{ sx: { fontSize: '1.05rem', fontWeight: 600 } }}
				/>
			</Stack>

			<Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
				{selectedSeverity && (
					<Stack direction="row" spacing={0.75} alignItems="center">
						<Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: selectedSeverity.color }} />
						<Typography variant="caption" color="text.secondary">{selectedSeverity.label}</Typography>
					</Stack>
				)}
				{!isNew && (
					<FormControlLabel
						control={<Switch size="small" checked={form.active} onChange={set('active')} />}
						label={<Typography variant="caption" color="text.secondary">{form.active ? 'Active' : 'Inactive'}</Typography>}
					/>
				)}
			</Stack>

			<Divider sx={{ mb: 1 }} />

			{saveError && <Alert severity="error" sx={{ mb: 2 }}>{saveError}</Alert>}

			{/* ============================================ */}
			{/* Tabs                                          */}
			{/* ============================================ */}
			<Tabs
				value={tab}
				onChange={(_, v) => setTab(v)}
				sx={{ mb: 3, minHeight: 40, '& .MuiTab-root': { minHeight: 40, py: 0.5 } }}
			>
				<Tab icon={<DashboardIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Overview" />
				<Tab icon={<FunctionsIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Data Source & Formulas" />
				<Tab icon={<TuneIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Detection" />
			</Tabs>

			{/* ============================================ */}
			{/* Tab 0 — Overview (read-only summary)          */}
			{/* ============================================ */}
			{tab === 0 && (
				<Box>
					<Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
						<Typography variant="subtitle2" fontWeight={700} sx={{ flex: 1 }}>Summary</Typography>
						{!isNew && (
							<Button size="small" startIcon={<ContentCopyIcon sx={{ fontSize: 15 }} />} onClick={handleDuplicate} disabled={saving}>
								Duplicate
							</Button>
						)}
					</Box>

					<Grid container spacing={2.5} sx={{ mb: 3 }}>
						<SummaryField label="Source table">{form.sourceTable || '—'}</SummaryField>
						<SummaryField label="Detection method">{selectedMethod ? selectedMethod.label : form.detectionMethod}</SummaryField>
						<SummaryField label="SLA threshold">{form.slaThreshold}</SummaryField>
						<SummaryField label="Direction">{selectedDirection ? (selectedDirection.value ? 'A rise indicates a problem' : 'A drop indicates a problem') : '—'}</SummaryField>
						<SummaryField label="Minimum volume">{form.minVolumeAbsolute}</SummaryField>
						<SummaryField label="Day-of-week baseline">{form.useDayOfWeek ? 'Yes' : 'No'}</SummaryField>
					</Grid>

					<Divider sx={{ mb: 2 }} />

					<Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>Usage</Typography>
					<Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
						{form.active
							? 'Rule active — evaluated by the detection engine every day.'
							: 'Rule inactive — not evaluated by the engine until reactivated.'}
					</Typography>
					<Typography variant="caption" color="text.disabled">
						You can test this configuration manually in the "Manual Engine Run" section below.
					</Typography>
				</Box>
			)}

			{/* ============================================ */}
			{/* Tab 1 — Data Source & Formulas                */}
			{/* ============================================ */}
			{tab === 1 && (
				<Box>
					<Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
						The table and columns for the raw counters, and the T-SQL expressions that compute the KPI and the volume.
					</Typography>
					<Grid container spacing={2}>
						<Grid item xs={12}>
							<TextField
								label="Counter Table"
								required
								value={form.sourceTable}
								onChange={set('sourceTable')}
								fullWidth
								size="small"
								placeholder="E.g.: VTK_S12_4G_DATA"
							/>
						</Grid>
						<Grid item xs={6} md={3}>
							<TextField
								label="Vendor Column" required
								value={form.sourceVendorCol} onChange={set('sourceVendorCol')}
								fullWidth size="small" placeholder="VENDOR"
							/>
						</Grid>
						<Grid item xs={6} md={3}>
							<TextField
								label="Site Column" required
								value={form.sourceSiteCol} onChange={set('sourceSiteCol')}
								fullWidth size="small" placeholder="JV_ID"
							/>
						</Grid>
						<Grid item xs={6} md={3}>
							<TextField
								label="Cell Column" required
								value={form.sourceCellCol} onChange={set('sourceCellCol')}
								fullWidth size="small" placeholder="MI_MYCOM_ID"
							/>
						</Grid>
						<Grid item xs={6} md={3}>
							<TextField
								label="Date Column" required
								value={form.sourceDateCol} onChange={set('sourceDateCol')}
								fullWidth size="small" placeholder="DATE"
							/>
						</Grid>

						<Grid item xs={12}>
							<Divider sx={{ my: 1 }} />
						</Grid>

						<Grid item xs={12}>
							<Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
								<Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>SQL Formulas</Typography>
								<Button
									size="small"
									variant="outlined"
									startIcon={validation.loading ? <CircularProgress size={14} /> : <PlayArrowIcon />}
									onClick={handleValidate}
									disabled={validation.loading || !form.sourceTable}
								>
									{validation.loading ? 'Validating...' : 'Validate'}
								</Button>
							</Box>
						</Grid>
						<Grid item xs={12}>
							<TextField
								label="KPI Formula"
								required
								value={form.kpiFormula}
								onChange={set('kpiFormula')}
								fullWidth size="small" multiline minRows={2} maxRows={5}
								placeholder="100.0 * SUM(ISNULL(num,0)) / NULLIF(SUM(ISNULL(den,0)), 0)"
								helperText="Use SUM() over the counters and protect divisions with NULLIF."
								inputProps={{ style: { fontFamily: 'monospace', fontSize: '0.82rem' } }}
							/>
						</Grid>
						<Grid item xs={12}>
							<TextField
								label="Volume Formula"
								required
								value={form.volFormula}
								onChange={set('volFormula')}
								fullWidth size="small"
								placeholder="SUM(ISNULL(denominator,0))"
								helperText="Usually the denominator of the KPI formula."
								inputProps={{ style: { fontFamily: 'monospace', fontSize: '0.82rem' } }}
							/>
						</Grid>

						<Grid item xs={12}>
							<Collapse in={!!validation.result}>
								{validation.result && (
									validation.result.isValid ? (
										<Alert severity="success" icon={<CheckCircleIcon fontSize="inherit" />} sx={{ py: 0.5 }}>
											Formula is valid.
											{validation.result.sampleKpi != null
												? ` Sample KPI: ${validation.result.sampleKpi.toFixed(3)}`
												: ''}
											{validation.result.sampleVolume != null
												? ` · Volume: ${Math.round(validation.result.sampleVolume)}`
												: ''}
										</Alert>
									) : (
										<Alert severity="error" sx={{ py: 0.5 }}>
											{validation.result.errorMessage}
										</Alert>
									)
								)}
							</Collapse>
						</Grid>
					</Grid>
				</Box>
			)}

			{/* ============================================ */}
			{/* Tab 2 — Detection                              */}
			{/* ============================================ */}
			{tab === 2 && (
				<Box>
					<Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
						<Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
							Alarm criteria, robustness filters and incident classification.
						</Typography>
						<TextField
							select
							size="small"
							value=""
							onChange={(e) => applyPreset(e.target.value)}
							sx={{ minWidth: 200 }}
							SelectProps={{
								displayEmpty: true,
								renderValue: () => (
									<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'text.secondary' }}>
										<AutoAwesomeIcon sx={{ fontSize: 16 }} />
										<span>Apply preset</span>
									</Box>
								),
							}}
						>
							{KPI_PROFILES.map((p) => (
								<MenuItem key={p.key} value={p.key}>{p.label}</MenuItem>
							))}
						</TextField>
					</Box>

					<Collapse in={!!appliedPreset}>
						<Alert severity="info" sx={{ mb: 2, py: 0.5 }} onClose={() => setAppliedPreset(null)}>
							Values from the "{appliedPreset}" preset applied below.
						</Alert>
					</Collapse>

					<Grid container spacing={2}>

						<SubGroupLabel>Alarm criteria</SubGroupLabel>
						<Grid item xs={12}>
							<TextField
								select
								label={<LabelWithTooltip text="Detection Method *" tooltip={TOOLTIPS.detectionMethod} />}
								value={form.detectionMethod}
								onChange={set('detectionMethod')}
								fullWidth size="small"
							>
								{DETECTION_METHOD_OPTIONS.map((o) => (
									<MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
								))}
							</TextField>
							{selectedMethod && (
								<Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5, lineHeight: 1.5 }}>
									{selectedMethod.description}
								</Typography>
							)}
						</Grid>

						<Grid item xs={12} md={6}>
							<TextField
								label={<LabelWithTooltip text="SLA Threshold *" tooltip={TOOLTIPS.slaThreshold} />}
								value={form.slaThreshold}
								onChange={setNum('slaThreshold')}
								fullWidth size="small" type="number"
								inputProps={{ step: 0.1 }}
								helperText="Absolute KPI limit (value or %)"
							/>
						</Grid>
						<Grid item xs={12} md={6}>
							<TextField
								select
								label={<LabelWithTooltip text="Direction *" tooltip={TOOLTIPS.direction} />}
								value={form.direction.toString()}
								onChange={setBool('direction')}
								fullWidth size="small"
							>
								{DIRECTION_OPTIONS.map((o) => (
									<MenuItem key={o.value.toString()} value={o.value.toString()}>{o.label}</MenuItem>
								))}
							</TextField>
						</Grid>

						<SubGroupLabel>Robustness filters</SubGroupLabel>
						<Grid item xs={12} md={6}>
							<TextField
								label={<LabelWithTooltip text="Minimum Volume" tooltip={TOOLTIPS.minVolumeAbsolute} />}
								value={form.minVolumeAbsolute}
								onChange={setNum('minVolumeAbsolute')}
								fullWidth size="small" type="number"
								inputProps={{ step: 5, min: 0 }}
								helperText="Statistical floor — not the production volume"
							/>
						</Grid>
						<Grid item xs={12} md={6}>
							<TextField
								select
								label={<LabelWithTooltip text="Day-of-Week Baseline" tooltip={TOOLTIPS.useDayOfWeek} />}
								value={form.useDayOfWeek.toString()}
								onChange={setBool('useDayOfWeek')}
								fullWidth size="small"
							>
								<MenuItem value="true">Yes — 12 weeks, same day</MenuItem>
								<MenuItem value="false">No — 28 consecutive days</MenuItem>
							</TextField>
						</Grid>

						<SubGroupLabel>Classification</SubGroupLabel>
						<Grid item xs={12}>
							<TextField
								select
								label={<LabelWithTooltip text="Severity" tooltip={TOOLTIPS.severity} />}
								value={form.severity}
								onChange={setNum('severity')}
								fullWidth size="small"
							>
								{SEVERITY_OPTIONS.map((s) => (
									<MenuItem key={s.value} value={s.value}>
										<Box component="span" sx={{
											display: 'inline-block', width: 8, height: 8,
											borderRadius: '50%', bgcolor: s.color, mr: 1,
										}} />
										{s.label}
									</MenuItem>
								))}
							</TextField>
						</Grid>
					</Grid>
				</Box>
			)}

			{/* ============================================ */}
			{/* Action buttons                                */}
			{/* ============================================ */}
			<Divider sx={{ mt: 3, mb: 2 }} />
			<Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
				<Box>
					{!isNew && (
						<Button
							variant="text"
							color="error"
							startIcon={<DeleteIcon sx={{ fontSize: 17 }} />}
							onClick={() => {
								if (window.confirm(`Delete rule "${form.kpiName}"?`)) onDelete(form.kpiId);
							}}
							disabled={saving}
						>
							Delete
						</Button>
					)}
				</Box>
				<Stack direction="row" spacing={1}>
					<Button variant="outlined" startIcon={<CloseIcon />} onClick={onCancel} disabled={saving}>
						Cancel
					</Button>
					<Button variant="contained" startIcon={<SaveIcon />} onClick={handleSave} disabled={saving}>
						{saving ? 'Saving...' : (isNew ? 'Create Rule' : 'Save Changes')}
					</Button>
				</Stack>
			</Box>
		</Box>
	);
}
